/**
 * Hook that gates attachment interactions behind an AttachmentWarningModal.
 *
 * On first click, it opens the modal instead of proceeding. Once the user
 * interacts with the modal (Go back, View attachment, or close), a session
 * cookie is set so subsequent attachment clicks proceed without interruption.
 *
 * Usage:
 *   const { isModalOpen, onAttachmentClick, onGoBack, onViewAttachment } = useAttachmentWarning();
 *   // Wrap your attachment click handler:
 *   onAttachmentClick(() => openImageViewer(zDocId));
 */
import { useCallback, useState } from 'react';
import { useCookies } from 'react-cookie';

const COOKIE_NAME = 'attachment_warning_dismissed';

export type UseAttachmentWarningResult = {
  isModalOpen: boolean;
  onAttachmentClick(proceed: () => void): void;
  onGoBack(): void;
  onViewAttachment(): void;
};

export default function useAttachmentWarning(): UseAttachmentWarningResult {
  const [cookies, setCookie] = useCookies([COOKIE_NAME]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const dismiss = useCallback(() => {
    // No maxAge/expires — creates a session cookie that clears when the browser closes
    setCookie(COOKIE_NAME, '1', { path: '/' });
  }, [setCookie]);

  const onAttachmentClick = useCallback(
    (proceed: () => void) => {
      if (cookies[COOKIE_NAME]) {
        proceed();
        return;
      }
      setPendingAction(() => proceed);
      setIsModalOpen(true);
    },
    [cookies],
  );

  const onGoBack = useCallback(() => {
    dismiss();
    setIsModalOpen(false);
    setPendingAction(null);
  }, [dismiss]);

  const onViewAttachment = useCallback(() => {
    dismiss();
    setIsModalOpen(false);
    pendingAction?.();
    setPendingAction(null);
  }, [dismiss, pendingAction]);

  return { isModalOpen, onAttachmentClick, onGoBack, onViewAttachment };
}
